/** Backend-served top-level config schema for the per-tenant editor surfaces.
 *
 * Thin selector over the SAME query the batch config page uses — identical
 * queryKey / queryFn / staleTime so the two pages share one cache entry:
 * GET /tour_operators/configs/schema/, narrowed to `.top.properties`. This is
 * the runtime source of truth that replaced the bundled `topconf_schema.json`
 * copy (the served schema is a strict superset of that file).
 */
import { useQuery } from '@tanstack/react-query';

import { getConfigSchemaBundle } from '@/api/tourOperatorConfigs';

import type { SchemaProp } from './schema-grid/types';

export interface TopConfigSchema {
  /** `top.properties` of the served bundle; undefined while loading / on error. */
  schemaProps: Record<string, SchemaProp> | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useTopConfigSchema(): TopConfigSchema {
  const query = useQuery({
    queryKey: ['tourOperators', 'configSchemaBundle'],
    queryFn: getConfigSchemaBundle,
    staleTime: 30 * 60_000,
  });
  return {
    schemaProps: query.data?.top.properties,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
